// Copyright (c) 2023, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 Inc. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { Theme } from "@mui/material";

const generateRandomColor = (): string => {
  return `#${Math.floor(Math.random() * 16777215).toString(16)}`;
};

export const generateRandomColors = (numberOfColors: number): string[] => {
  return Array.from({ length: numberOfColors }, () => generateRandomColor());
};

export const generateThemeMatchingColors = (theme: Theme, numberOfColors: number): string[] => {
  const paletteColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.error.main,
    theme.palette.info.main,
    theme.palette.success.main,
  ];

  const result: string[] = [];

  for (let i = 0; i < numberOfColors; i++) {
    const baseColor = paletteColors[i % paletteColors.length];
    const variation = Math.random() * 0.3 - 0.15; // Vary by ±15%

    const rgb = hexToRgb(baseColor);
    if (rgb) {
      const newRgb = rgb.map(value => 
        Math.max(0, Math.min(255, Math.round(value * (1 + variation))))
      );
      result.push(rgbToHex(newRgb[0], newRgb[1], newRgb[2]));
    } else {
      result.push(generateRandomColor());
    }
  }

  return result;
};

// Helper function to convert hex to RGB
const hexToRgb = (hex: string): number[] | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : null;
};

// Helper function to convert RGB to hex
const rgbToHex = (r: number, g: number, b: number): string => {
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};
